import { supabase } from '../lib/supabase';

/**
 * Professional Client Authentication Service
 * Handles signup, login, and profile management for professional clients
 */

export const professionalClientAuthService = {
  /**
   * Sign up a new professional client
   * @param {Object} signupData - Professional client signup data
   * @returns {Promise<Object>} Created professional client and auth user
   */
  async signUp(signupData) {
    try {
      const { companyName, contactName, email, password, phone, clientType } = signupData;

      // Create auth user with professional metadata
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location?.origin + '/professional-client-dashboard',
          data: {
            account_type: 'professional',
            company_name: companyName,
            contact_name: contactName,
            phone: phone || null,
            client_type: clientType || 'other'
          }
        }
      });

      if (authError) {
        // Log the actual error for debugging
        console.error('Supabase auth.signUp error:', {
          message: authError?.message,
          status: authError?.status,
          code: authError?.code,
          name: authError?.name
        });

        // Handle specific error types with detailed guidance
        if (authError?.message?.includes('rate limit') || authError?.message?.includes('Too many') || authError?.status === 429) {
          const rateLimitError = new Error(
            'Trop de tentatives d\'inscription détectées. Pour votre sécurité, veuillez patienter 5-10 minutes avant de réessayer. Si le problème persiste, contactez-nous au support.'
          );
          rateLimitError.code = 'RATE_LIMIT';
          rateLimitError.retryAfter = 300; // 5 minutes in seconds
          throw rateLimitError;
        }
        
        // Check for duplicate email (most common issue)
        if (authError?.message?.includes('already registered') || 
            authError?.message?.includes('already been registered') ||
            authError?.message?.includes('User already registered')) {
          throw new Error('Cette adresse email est déjà enregistrée. Veuillez vous connecter ou utiliser une autre adresse.');
        }
        
        // Check for invalid email format
        if (authError?.message?.includes('invalid email') || 
            authError?.message?.includes('Invalid email')) {
          throw new Error('Format d\'email invalide. Veuillez vérifier votre adresse email.');
        }
        
        // Check for password requirements
        if (authError?.message?.includes('password')) {
          throw new Error('Le mot de passe ne respecte pas les critères de sécurité. Utilisez au moins 6 caractères.');
        }
        
        // Check for email confirmation disabled
        if (authError?.message?.includes('Email link is invalid') || 
            authError?.message?.includes('Signups not allowed')) {
          throw new Error('Les inscriptions sont temporairement désactivées. Veuillez contacter le support.');
        }
        
        // Generic auth error - show the actual Supabase message for transparency
        throw new Error(authError?.message || 'Erreur d\'authentification. Veuillez réessayer.');
      }

      // Check if user was created successfully
      if (!authData?.user) {
        throw new Error('Erreur lors de la création du compte. Veuillez réessayer.');
      }

      // Create professional_clients record directly (no longer using trigger)
      const { data: professionalClient, error: insertError } = await supabase
        ?.from('professional_clients')
        ?.insert({
          auth_user_id: authData?.user?.id,
          company_name: companyName,
          contact_name: contactName,
          email: email,
          phone: phone || null,
          client_type: clientType || 'other',
          is_active: true
        })
        ?.select()
        ?.single();

      if (insertError) {
        console.error('Error creating professional client record:', insertError);
        // Log the error but don't fail signup - user can still access their account
        // Admin can manually create the professional_clients record if needed
      }

      return {
        success: true,
        user: authData?.user,
        professionalClient: professionalClient ? {
          id: professionalClient?.id,
          authUserId: professionalClient?.auth_user_id,
          companyName: professionalClient?.company_name,
          contactName: professionalClient?.contact_name,
          email: professionalClient?.email,
          phone: professionalClient?.phone,
          clientType: professionalClient?.client_type,
          isActive: professionalClient?.is_active,
          createdAt: professionalClient?.created_at
        } : null
      };
    } catch (error) {
      console.error('Professional client signup error:', error);
      // Ensure we throw a user-friendly error message
      if (error?.message) {
        throw error;
      }
      throw new Error('Erreur lors de la création du compte. Veuillez réessayer.');
    }
  },

  /**
   * Sign in professional client
   * @param {string} email - Professional client email
   * @param {string} password - Password
   * @returns {Promise<Object>} Authenticated user and professional client data
   */
  async signIn(email, password) {
    try {
      const { data: authData, error: authError } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (authError) {
        return { success: false, error: authError?.message || 'Erreur d\'authentification' };
      }

      // Fetch professional client data
      const { data: professionalClient, error: fetchError } = await supabase
        ?.from('professional_clients')
        ?.select('*')
        ?.eq('auth_user_id', authData?.user?.id)
        ?.maybeSingle();

      if (fetchError) {
        console.error('Error fetching professional client:', fetchError);
        return { success: false, error: 'Compte professionnel introuvable' };
      }

      if (!professionalClient) {
        return { success: false, error: 'Aucun compte professionnel associé à cet utilisateur' };
      }

      // Check if account is explicitly set to inactive (not null or undefined)
      if (professionalClient?.is_active === false) {
        return { success: false, error: 'Votre compte professionnel est inactif' };
      }

      return {
        success: true,
        user: authData?.user,
        session: authData?.session,
        professionalClient: {
          id: professionalClient?.id,
          authUserId: professionalClient?.auth_user_id,
          companyName: professionalClient?.company_name,
          contactName: professionalClient?.contact_name,
          email: professionalClient?.email,
          phone: professionalClient?.phone,
          clientType: professionalClient?.client_type,
          isActive: professionalClient?.is_active,
          createdAt: professionalClient?.created_at
        }
      };
    } catch (error) {
      console.error('Professional client signin error:', error);
      return { success: false, error: error?.message || 'Erreur de connexion' };
    }
  },

  /**
   * Sign out professional client
   * @returns {Promise<void>}
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Professional client signout error:', error);
      throw error;
    }
  },

  /**
   * Get current authenticated professional client
   * @returns {Promise<Object>} Current professional client data
   */
  async getCurrentProfessionalClient() {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) return null;

      const { data: professionalClient, error: fetchError } = await supabase
        ?.from('professional_clients')
        ?.select('*')
        ?.eq('auth_user_id', user?.id)
        ?.maybeSingle();

      if (fetchError) {
        console.error('Error fetching professional client:', fetchError);
        return null;
      }

      return {
        id: professionalClient?.id,
        authUserId: professionalClient?.auth_user_id,
        companyName: professionalClient?.company_name,
        contactName: professionalClient?.contact_name,
        email: professionalClient?.email,
        phone: professionalClient?.phone,
        clientType: professionalClient?.client_type,
        isActive: professionalClient?.is_active,
        createdAt: professionalClient?.created_at
      };
    } catch (error) {
      console.error('Error getting current professional client:', error);
      return null;
    }
  },

  /**
   * Update professional client profile
   * @param {Object} updateData - Profile update data
   * @returns {Promise<Object>} Updated professional client
   */
  async updateProfile(updateData) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      const { companyName, contactName, phone, clientType } = updateData;

      const { data, error } = await supabase
        ?.from('professional_clients')
        ?.update({
          company_name: companyName,
          contact_name: contactName,
          phone: phone || null,
          client_type: clientType,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('auth_user_id', user?.id)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;

      return {
        success: true,
        professionalClient: {
          id: data?.id,
          authUserId: data?.auth_user_id,
          companyName: data?.company_name,
          contactName: data?.contact_name,
          email: data?.email,
          phone: data?.phone,
          clientType: data?.client_type,
          isActive: data?.is_active,
          createdAt: data?.created_at
        }
      };
    } catch (error) {
      console.error('Error updating professional client profile:', error);
      throw error;
    }
  },

  /**
   * Reset password for professional client
   * @param {string} email - Professional client email
   * @returns {Promise<void>}
   */
  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window?.location?.origin}/professional-client/reset-password`
      });

      if (error) throw error;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  },

  /**
   * Update password for professional client
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async updatePassword(newPassword) {
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;
    } catch (error) {
      console.error('Password update error:', error);
      throw error;
    }
  }
};

export default professionalClientAuthService;